/**
 * Format a price in any ISO-4217 currency for Indian / Western locales.
 * Falls back to "INR" for legacy listings without a currency field.
 */
const CURRENCY_SYMBOLS = {
    INR: "₹", USD: "$", GBP: "£", EUR: "€", JPY: "¥", AUD: "A$",
    CAD: "C$", NZD: "NZ$", CHF: "Fr", CNY: "¥", HKD: "HK$", SGD: "S$",
    AED: "د.إ", SAR: "ر.س", QAR: "ر.ق", KWD: "د.ك", BHD: "د.ب", OMR: "ر.ع.",
    PKR: "₨", BDT: "৳", LKR: "Rs", NPR: "₨", BTN: "Nu", MVR: "ރ",
    THB: "฿", VND: "₫", IDR: "Rp", MYR: "RM", PHP: "₱", KRW: "₩", TWD: "NT$",
    BRL: "R$", MXN: "$", ARS: "$", CLP: "$", COP: "$", PEN: "S/", VES: "Bs",
    ZAR: "R", NGN: "₦", KES: "KSh", GHS: "₵", EGP: "£", MAD: "د.م.",
    TRY: "₺", RUB: "₽", UAH: "₴", PLN: "zł", SEK: "kr", NOK: "kr", DKK: "kr",
    CZK: "Kč", HUF: "Ft", RON: "lei", ILS: "₪",
};

export function currencySymbol(code) {
    if (!code) return "₹";
    return CURRENCY_SYMBOLS[code.toUpperCase()] || code.toUpperCase() + " ";
}

export function formatPrice(price, currency = "INR") {
    // A price of 0 (or missing) means the seller didn't set one — typical for
    // services, "open to offers", or community listings. Show a neutral
    // "Contact for price" label instead of a misleading ₹0 / $0 number.
    const n = Number(price);
    if (!Number.isFinite(n) || n <= 0) return "Contact for price";
    const sym = currencySymbol(currency);
    const isINR = (currency || "INR").toUpperCase() === "INR";
    const formatted = new Intl.NumberFormat(
        isINR ? "en-IN" : "en-US",
        { maximumFractionDigits: 0 }
    ).format(n);
    return `${sym}${sym.length > 1 ? " " : ""}${formatted}`;
}

/**
 * v77.22 — Compensation formatter for `jobs` + `job-seeker` listings.
 * Renders e.g. "₹8,00,000 / yr", "$50 / hr", "₹12,000 / project".
 * Falls back to a category-appropriate "private" label when the
 * candidate / employer leaves the amount blank.
 */
const COMPENSATION_PERIOD_LABELS = {
    per_year:    "yr",
    per_month:   "mo",
    per_week:    "wk",
    per_day:     "day",
    per_hour:    "hr",
    per_project: "project",
    per_man_day: "man-day",
};

/**
 * v77.23 — Compact currency renderer. For INR uses Lakh / Crore so a
 * range like "₹8,00,000 – ₹15,00,000 / yr" collapses to a card-friendly
 * "₹8L – ₹15L / yr". For other currencies uses K / M / B.
 */
function compactAmount(n, currency) {
    if (!Number.isFinite(n) || n <= 0) return null;
    const cur = (currency || "INR").toUpperCase();
    const sym = currencySymbol(cur);
    const space = sym.length > 1 ? " " : "";
    if (cur === "INR") {
        if (n >= 1e7) {
            const v = n / 1e7;
            return `${sym}${space}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, "")}Cr`;
        }
        if (n >= 1e5) {
            const v = n / 1e5;
            return `${sym}${space}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, "")}L`;
        }
        if (n >= 1e3) {
            const v = n / 1e3;
            return `${sym}${space}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, "")}K`;
        }
    } else {
        if (n >= 1e9) return `${sym}${space}${(n / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
        if (n >= 1e6) return `${sym}${space}${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
        if (n >= 1e3) return `${sym}${space}${(n / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
    }
    const isINR = cur === "INR";
    const formatted = new Intl.NumberFormat(
        isINR ? "en-IN" : "en-US",
        { maximumFractionDigits: 0 }
    ).format(n);
    return `${sym}${space}${formatted}`;
}

export function formatCompensation(price, currency = "INR", period = "per_year", category = "jobs") {
    const n = Number(price);
    const suffix = COMPENSATION_PERIOD_LABELS[period] || COMPENSATION_PERIOD_LABELS.per_year;
    if (!Number.isFinite(n) || n <= 0) {
        // v79.173 — Founder direction: "no need to say 'Compensation
        // discussed on shortlist'. Ignore it if salary offer is not
        // mentioned." Return empty string so callers render nothing
        // instead of a placeholder band. Open-for-Work keeps the
        // "Current pay · private" hint because that IS meaningful UX
        // for the candidate profile.
        return category === "job-seeker" ? "Current pay · private" : "";
    }
    return `${compactAmount(n, currency)} / ${suffix}`;
}

/**
 * v77.23 — Compensation RANGE formatter. Renders a band like
 *   "₹8L – ₹15L / yr"   (both sides filled)
 *   "From ₹8L / yr"      (only min filled)
 *   "Up to ₹15L / yr"    (only max filled)
 * Falls back to the single-value formatter when neither bound is set.
 */
export function formatCompensationRange(min, max, currency = "INR", period = "per_year", category = "jobs") {
    const lo = Number(min);
    const hi = Number(max);
    const loOk = Number.isFinite(lo) && lo > 0;
    const hiOk = Number.isFinite(hi) && hi > 0;
    const suffix = COMPENSATION_PERIOD_LABELS[period] || COMPENSATION_PERIOD_LABELS.per_year;
    if (!loOk && !hiOk) {
        // v79.173 — See formatCompensation() note. Empty string → callers
        // render nothing instead of a placeholder salary band.
        return category === "job-seeker" ? "Current pay · private" : "";
    }
    if (loOk && hiOk) {
        // If min === max (or max < min, treat as single value)
        if (Math.abs(hi - lo) < 0.001 || hi < lo) return `${compactAmount(lo, currency)} / ${suffix}`;
        return `${compactAmount(lo, currency)} – ${compactAmount(hi, currency)} / ${suffix}`;
    }
    if (loOk) return `From ${compactAmount(lo, currency)} / ${suffix}`;
    return `Up to ${compactAmount(hi, currency)} / ${suffix}`;
}

